import * as moment from "moment";
import { GetQuickJournalNow } from "../../Data/Clock/QuickJournalClock";
import {
    GetDayHourKey,
    GetLocalDayKeyFromDate,
} from "../../Data/QuickJournalKeys";
import {
    quickJournalHeatmapHours,
    quickJournalHeatmapRows,
} from "../../Data/QuickJournalTypes";
import { quickJournalTheme, type QuickJournalTheme } from "../QuickJournalTheme";

export const quickJournalHeatmapFlashDurationInMilliseconds = 1800;
export const quickJournalHeatmapTooltipId = "quickJournalHeatmapTooltip";
export function GetQuickJournalHeatmapContainerStyle(
    theme: QuickJournalTheme = quickJournalTheme
): React.CSSProperties {
    return {
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "4px",
        marginTop: "0px",
        marginBottom: "4px",
        color: theme.heatmap.containerColor,
        userSelect: "none",
        pointerEvents: "auto",
    };
}

export function GetQuickJournalHeatmapGridStyle(): React.CSSProperties {
    return {
        display: "grid",
        gap: "2px",
        width: "100%",
        maxWidth: "232px",
        padding: "4px 6px 5px",
        borderRadius: "10px",
        pointerEvents: "auto",
    };
}

const quickJournalWeekdayLabels = [
    "Sun",
    "Mon",
    "Tue",
    "Wed",
    "Thu",
    "Fri",
    "Sat",
];

export function BuildQuickJournalHeatmapCells(
    weeklyHourlyCounts: Record<string, number>
): {
    rows: Array<{
        rowLabel: string;
        dayKey: string;
        cells: Array<{
            cellKey: string;
            dayKey: string;
            hour: number;
            level: number;
            tooltip: string;
        }>;
    }>;
    hasAnyActivity: boolean;
    maxCount: number;
} {
    const maxCount = Math.max(0, ...Object.values(weeklyHourlyCounts));
    const rows: Array<{
        rowLabel: string;
        dayKey: string;
        cells: Array<{
            cellKey: string;
            dayKey: string;
            hour: number;
            level: number;
            tooltip: string;
        }>;
    }> = [];
    let hasAnyActivity = false;
    const today = GetQuickJournalNow();
    today.setHours(12, 0, 0, 0);

    for (let rowIndex = 0; rowIndex < quickJournalHeatmapRows; rowIndex++) {
        const day = new Date(today);
        day.setDate(today.getDate() - rowIndex);
        const dayKey = GetLocalDayKeyFromDate(day);
        const rowLabel = quickJournalWeekdayLabels[day.getDay()];
        const cells: Array<{
            cellKey: string;
            dayKey: string;
            hour: number;
            level: number;
            tooltip: string;
        }> = [];

        for (let hour = 0; hour < quickJournalHeatmapHours; hour++) {
            const cellKey = GetDayHourKey(dayKey, hour);
            const count = weeklyHourlyCounts[cellKey] ?? 0;
            const level =
                count <= 0 || maxCount <= 0
                    ? 0
                    : Math.min(4, Math.max(1, Math.ceil((count / maxCount) * 4)));

            if (level > 0) {
                hasAnyActivity = true;
            }

            cells.push({
                cellKey,
                dayKey,
                hour,
                level,
                tooltip: BuildQuickJournalHeatmapCellTooltip(dayKey, hour),
            });
        }

        rows.push({ rowLabel, dayKey, cells });
    }

    return { rows, hasAnyActivity, maxCount };
}

export function GetQuickJournalHeatmapCellColor(
    level: number,
    theme: QuickJournalTheme = quickJournalTheme
): string {
    return theme.heatmap.cellBackground(level);
}

function BuildQuickJournalHeatmapCellTooltip(
    dayKey: string,
    hour: number
): string {
    const [yearText, monthText, dayText] = dayKey.split("-");
    const year = Number(yearText);
    const month = Number(monthText);
    const day = Number(dayText);

    if (!year || !month || !day) {
        return "";
    }

    const dayLabel = moment(
        new Date(year, month - 1, day, 12, 0, 0, 0)
    ).format("ddd MMM D YYYY");
    const startHour = `${hour}`.padStart(2, "0");
    const endHour = `${hour + 1}`.padStart(2, "0");

    return `${dayLabel} ${startHour}00-${endHour}00`;
}
